import type { InterfaceAddress } from '@interfaces/InterfaceAddress.ts';
import type { InterfacePhoneNumber } from '@interfaces/InterfacePhoneNumber.ts';
import type { InterfaceUser } from '@interfaces/InterfaceUser.ts';
import { createSlice } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import { sliceStateCheckAndUpdate } from '@store/reducers/reducerHelpers/sliceStateCheckAndUpdate.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';
import memoize from 'lodash-es/memoize';

export interface UserState {
  users: { [uuid: string]: InterfaceUser };
  adminUuids: string[];
}

const initialState: UserState = {
  users: {},
  adminUuids: [],
};

const sliceName = 'user';
export const usersSlice = createSlice({
  name: sliceName,
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(meLogOut.fulfilled, () => initialState)
      .addCase(getCompanyUsers.fulfilled, (state, action) => {
        // Handle both array and object responses
        if (Array.isArray(action.payload)) {
          // Convert array to object keyed by UUID
          const usersObject: { [uuid: string]: InterfaceUser } = {};
          action.payload.forEach((user: InterfaceUser) => {
            if (user.uuid) {
              usersObject[user.uuid] = user;
            }
          });
          state.users = usersObject;
        } else if (action.payload && typeof action.payload === 'object') {
          // If it's already an object, use it directly
          state.users = action.payload;
        }
      })
      .addCase(getCompanyAdminUserUuid.fulfilled, sliceStateCheckAndUpdate.sliceItem('adminUuids'))
      .addCase(updateUserStatus.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('users', 'uuid'))
      .addCase(createUser.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('users', 'uuid'))
      .addCase(updateUser.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('users', 'uuid'))
      .addCase(deleteUser.fulfilled, (state, action) => {
        // Remove the user from the state
        if (action.meta.arg?.urlVarsReplaceMap) {
          const userUuid = action.meta.arg.urlVarsReplaceMap['{userUuid}'];
          if (userUuid && state.users[userUuid]) {
            delete state.users[userUuid];
          }
        }
      })
      // User Profile Management
      .addCase(getUserProfile.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('users', 'uuid'))
      .addCase(updateUserProfile.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('users', 'uuid'))
      // User Address Management - these will need separate state if needed
      .addCase(getUserAddresses.fulfilled, () => {
        // Address management could be added to user state if needed
      })
      // User Document Management - these will need separate state if needed
      .addCase(getUserDocuments.fulfilled, () => {
        // Document management could be added to user state if needed
      })
      // Profile Picture Management
      .addCase(updateUserProfilePicture.fulfilled, (state, action) => {
        // Update user's profile picture URL if returned
        if (action.meta.arg?.urlVarsReplaceMap && action.payload?.profilePictureUrl) {
          const userUuid = action.meta.arg.urlVarsReplaceMap['{userUuid}'];
          if (userUuid && state.users[userUuid]) {
            state.users[userUuid].imgUrl = action.payload.profilePictureUrl;
          }
        }
      })
      .addCase(deleteUserProfilePicture.fulfilled, (state, action) => {
        // Remove user's profile picture URL
        if (action.meta.arg?.urlVarsReplaceMap) {
          const userUuid = action.meta.arg.urlVarsReplaceMap['{userUuid}'];
          if (userUuid && state.users[userUuid]) {
            state.users[userUuid].imgUrl = '';
          }
        }
      })
      // User Role Management - these will need separate state if needed
      .addCase(getUserRoles.fulfilled, () => {
        // Role management could be added to user state if needed
      });
  },
});

export const getCompanyUsers = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/user');
export const getCompanyAdminUserUuid = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/user/admin/uuid');

export interface InterfaceJoinBetaProgramForm {
  name: string;
  phoneNumber: string;
  email: string;
  random_things: string;
  message: string;
}

export const joinBetaProgram = thunkPost.post<InterfaceJoinBetaProgramForm>(sliceName, `/email/join`);
export const getUsers = thunkGet.compUrl(sliceName, `c/{companySlug}/admin/user`);

export interface InterfaceUpdateUserStatusForm {
  userId: string;
  status: string;
}

export const updateUserStatus = thunkPost.compUrl<InterfaceUpdateUserStatusForm, { '{userId}': string }>(
  sliceName,
  `c/{companySlug}/admin/user/{userId}/status`,
);

// ============= NEW CRUD OPERATIONS =============

// Create User Interface
export interface InterfaceCreateUserForm {
  email: string;
  firstname: string;
  lastname: string;
  phoneNumbers?: InterfacePhoneNumber[];
  defaultCountryIsoCode?: string;
  role?: string;
}

// Update User Interface
export interface InterfaceUpdateUserForm {
  email?: string;
  firstname?: string;
  lastname?: string;
  phoneNumbers?: InterfacePhoneNumber[];
  defaultCountryIsoCode?: string;
  imgUrl?: string;
}

// Create User
export const createUser = thunkPost.compUrl<InterfaceCreateUserForm>(sliceName, '/c/{companySlug}/admin/user');

// Update User (Full Update)
export const updateUser = thunkPut.compUrl<InterfaceUpdateUserForm, { '{userUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}',
);

// Delete User
export const deleteUser = thunkDelete.compUrl<undefined, { '{userUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}',
);

// ============= USER PROFILE MANAGEMENT =============

// Profile Management Interfaces
export interface InterfaceUpdateUserProfileForm {
  firstname?: string;
  lastname?: string;
  email?: string;
  phoneNumber?: string;
  defaultCountryIsoCode?: string;
}

export interface InterfaceChangePasswordForm {
  oldPassword: string;
  password: string;
  password_confirmation: string;
}

export interface InterfaceUploadDocumentForm {
  file: File;
  documentTypeSlug: string;
}

export interface InterfaceUpdateProfilePictureForm {
  file: File;
}

// User Profile Endpoints
export const getUserProfile = thunkGet.compUrl<undefined>(sliceName, '/c/{companySlug}/admin/user/{userUuid}/profile');

export const updateUserProfile = thunkPut.compUrl<InterfaceUpdateUserProfileForm>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/profile',
);

export const changeUserPassword = thunkPost.compUrl<InterfaceChangePasswordForm>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/change-password',
);

// User Address Management
export const getUserAddresses = thunkGet.compUrl<undefined>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/address',
);

export const addUserAddress = thunkPost.compUrl<InterfaceAddress>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/address',
);

export const updateUserAddress = thunkPut.compUrl<InterfaceAddress>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/address/{addressUuid}',
);

export const deleteUserAddress = thunkDelete.compUrl(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/address/{addressUuid}',
);

// User Document Management
export const getUserDocuments = thunkGet.compUrl<undefined>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/document',
);

export const uploadUserDocument = thunkPost.compUrl<InterfaceUploadDocumentForm>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/document',
);

export const getUserDocument = thunkGet.compUrl<undefined>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/document/{documentTypeSlug}',
);

export const deleteUserDocument = thunkDelete.compUrl(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/document/{documentTypeSlug}',
);

// Profile Picture Management
export const updateUserProfilePicture = thunkPost.compUrl<InterfaceUpdateProfilePictureForm>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/profile-picture',
);

export const getUserProfilePicture = thunkGet.compUrl<undefined>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/profile-picture/{format}',
);

export const deleteUserProfilePicture = thunkDelete.compUrl(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/profile-picture',
);

// User Companies and Roles
export const getUserCompanies = thunkGet.compUrl<undefined>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/company',
);

export const getUserRoles = thunkGet.compUrl<undefined>(sliceName, '/c/{companySlug}/admin/user/{userUuid}/role');

export const assignUserRole = thunkPost.compUrl<{ role: string }>(
  sliceName,
  '/c/{companySlug}/admin/user/{userUuid}/role',
);

export const removeUserRole = thunkDelete.compUrl(sliceName, '/c/{companySlug}/admin/user/{userUuid}/role/{role}');

export const selectDriverName = memoize((state: AppState, driverUuid: string) => {
  const user = selectUserState(state).users[driverUuid];
  return user ? `${user.firstname} ${user.lastname}` : null;
});

// Action creators are generated for each case reducer function
// export const { setUsers, setUser, setSearch } = usersSlice.actions;

export const selectUserState = (state: AppState) => state.user;
export const selectUsers = memoize((state: AppState) => selectUserState(state).users);

export default usersSlice.reducer;
